import { motion } from "framer-motion";

const AIRPORT_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3aeb2b71-c3a3-4775-b2b3-77824c8c9518/images/a82ac91d37e82f47676f2fa73751baa608853e060bb029a342399e0afe65f23b.jpeg";

const HIGHLIGHTS = [
  {
    title: "Flight-Aware Pickups",
    body: "We monitor your inbound flight in real time and adjust automatically for early arrivals and delays.",
  },
  {
    title: "Meet & Greet",
    body: "A chauffeur waiting inside the terminal with your name — quietly, and without fuss.",
  },
  {
    title: "Corporate Accounts",
    body: "Consolidated monthly invoicing, cost centres, and traveller profiles for executive assistants.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export default function AirportCorporate() {
  return (
    <section
      data-testid="airport-corporate-section"
      className="bg-ivory py-24 md:py-36"
    >
      <div className="max-w-[92rem] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
        {/* LEFT — full-bleed editorial image */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-6 relative"
        >
          <div className="relative overflow-hidden bg-stone">
            <img
              src={AIRPORT_IMG}
              alt="Black executive sedan arriving at a hotel entrance in warm morning light"
              className="w-full h-[480px] md:h-[620px] object-cover"
              loading="lazy"
            />
          </div>
          <div className="absolute -bottom-6 -right-4 md:-right-8 hidden md:block">
            <div className="bg-midnight text-warm-white px-8 py-6 max-w-xs">
              <p className="sjc-eyebrow text-gold">Guarantee</p>
              <p className="mt-3 font-display text-2xl leading-tight">
                We arrive fifteen minutes ahead of your scheduled pickup, as standard.
              </p>
            </div>
          </div>
        </motion.div>

        {/* RIGHT — editorial copy */}
        <div className="lg:col-span-6">
          <motion.p
            {...fadeUp}
            className="sjc-eyebrow text-muted-light flex items-center gap-4"
          >
            <span className="sjc-hairline w-8" />
            Airport & Corporate Travel
          </motion.p>

          <motion.h2
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="font-display mt-6 text-charcoal text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.015em]"
          >
            Precision that
            <br />
            <span className="italic font-light">removes the guesswork.</span>
          </motion.h2>

          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.18 }}
            className="mt-8 text-muted-light text-base md:text-lg leading-[1.75] font-light max-w-lg"
          >
            The difference between a good chauffeur and a great one is measured in the details.
            We plan around your calendar, flight number and preferred routing so that arrival
            and departure never demand any of your attention.
          </motion.p>

          <ul className="mt-14 space-y-9">
            {HIGHLIGHTS.map((h, i) => (
              <motion.li
                key={h.title}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: 0.1 * i, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`airport-highlight-${i}`}
                className="flex gap-6"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-gold mt-3 flex-shrink-0" />
                <div>
                  <h3 className="font-display text-2xl md:text-[26px] tracking-tight text-charcoal">
                    {h.title}
                  </h3>
                  <p className="mt-2 text-muted-light text-[15px] md:text-base leading-relaxed font-light max-w-md">
                    {h.body}
                  </p>
                </div>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
